import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useApolloClient, useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { A, paths } from '../../routing';
import InputTitle from './components/statusDropdown/InputTitle';
import { graphqlErrorsToUiErrors, queries, queriesV2 } from '../../api';
import { useTeamInfo } from '../../team-id-context';
import { useDealId } from '../../deal-id-context';
import { useModals } from '../../contexts/ModalsContext';
import PrivateLayout from '../../components/private-layout';
import LoadingSpinner from '../../components/loading-spinner';
import { CardContainer } from '../../components/card';
import DocumentTable from '../../components/document-table/DocumentTable';
import ActivitiesCard from '../../components/card/ActivitiesCard';
import ChecklistProgressCard from '../../components/checklist/ChecklistProgressCard';
import ChecklistSlideout from '../../components/checklist/ChecklistSlideout';
import CheckDealExists from './checkDealExists';
import Header from './header';
import Controls from './controls';
import DocumentTemplateModal from './document-template-modal/DocumentTemplateModalContainer';
import CreateBlankDocumentModal from './document-template-modal/CreateBlankDocumentModalContainer';
import { useDeal } from '../../contexts/DealContext';
import { useDocumentTeamPermissions } from '../document-wizard/shared/hooks/use-document-team-permissions/use-document-team-permissions';
import styles from './styles.module.scss';
import { FileUploadModal } from '../../components/shared/modals';
import { CreateDocumentModalsEnum, ModalActionsEnum } from '../../components/document-table/DocumentTableConstants';
import { useDealRoles } from '../../shared/hooks/useDealRoles/useDealRoles';
import { DealType } from '../../types/deals';
import { Document } from '../../gql/graphql';
import { ReactComponent as ArrowLeftIcon } from '../../shared/icons/arrow-left.svg';
import { ContentBody } from '../../components/private-layout/ContentBody';
import DealNotificationToggle from './components/dealNotification/DealNotificationToggle';
import NotFound from '../not-found';
import ErrorMessage from '../../components/error-message';
import { useUserRestrictions } from '../../auth/user-restrictions';
import SubscriptionRequiredModal from '../modals/subscriptionModal/SubscriptionRequiredModal';

const Page = () => {
  const { setCurrentDeal } = useDeal();
  const apolloClient = useApolloClient();
  const { formatMessage } = useIntl();
  const { teamId } = useTeamInfo();
  const dealId = useDealId();
  const { isCreator, isDealRolesLoading } = useDealRoles(dealId);
  const { activeModal, setActiveModal, closeDocumentModals } = useModals();
  const permissions = useDocumentTeamPermissions({ apolloClient, teamId });
  const { hasAccessToTemplates, canCreateTemplates, hasAccessToChecklists } = useUserRestrictions();
  const [isChecklistSlideoutOpen, setIsChecklistSlideoutOpen] = useState(false);
  const [isSubscriptionModalActive, setSubscriptionModalActive] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open checklist slideout when navigated from global search
  useEffect(() => {
    if (searchParams.get('openChecklist') === 'true' && hasAccessToChecklists) {
      setIsChecklistSlideoutOpen(true);
      // Remove the param from URL to prevent re-opening on re-renders
      searchParams.delete('openChecklist');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, hasAccessToChecklists, setSearchParams]);


  const {
    loading: isDealLoading,
    data: dealData,
    refetch: refetchDealData,
  } = useQuery(queries.getDealById, {
    variables: {
      dealId,
    },
    fetchPolicy: 'cache-and-network', // Return cache immediately, then refetch for fresh data
  });


  const { loading: isDealDocumentsDataLoading, data: dealDocumentsData } = useQuery(queriesV2.getDocumentsByDeal, {
    variables: {
      dealId,
    },
    fetchPolicy: 'cache-first', // Load from cache first, prevents double spinner from refetch
  });


  const { loading: alertableDocumentsDataLoading, data: alertableDocuments } = useQuery(
    queriesV2.getAlertableDocumentsByTeam,
    {
      variables: {
        teamId,
        dealId,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const {
    loading: loadingUser,
    data: userData,
    error,
    refetch: refetchUser,
  } = useQuery(queries.getCurrentUserBasic, {
    fetchPolicy: 'network-only',
  });

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS (Rules of Hooks)
  const refreshUserData = useCallback(async () => {
    await refetchUser();
  }, [refetchUser]);

  const refreshDealData = useCallback(async () => {
    await refetchDealData();
  }, [refetchDealData]);

  const handleChecklistClick = useCallback(() => {
    if (hasAccessToChecklists) {
      setIsChecklistSlideoutOpen(true);
    } else {
      setSubscriptionModalActive(true);
    }
  }, [hasAccessToChecklists]);

  // Sync deal data to DealContext when it changes
  // Note: We always update when dealData changes to ensure status and other
  // fields are fresh after navigating back from documents
  useEffect(() => {
    if (!dealData?.getDealById) {
      return;
    }
    setCurrentDeal(dealData.getDealById as unknown as DealType);
  }, [setCurrentDeal, dealData]);

  useEffect(() => {
    closeDocumentModals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const userDealNotifications = useMemo(() => {
    return userData?.currentUser?.communicationPreferences.deal_notifications;
  }, [userData?.currentUser?.communicationPreferences.deal_notifications]);

  const canEdit = permissions.hasEditPermission;

  // NOW safe to do conditional returns after ALL hooks have been called
  // Early return during loading - matches pattern used in landing.tsx, templates, tracker pages
  if (isDealLoading || isDealDocumentsDataLoading || loadingUser) {
    return <LoadingSpinner />;
  }

  // Error handling after loading check
  if (!userData) {
    const errors = graphqlErrorsToUiErrors(error);
    console.warn('ERROR', errors);
    if (errors._general?.id === 'api.error.not-found') {
      return <NotFound />;
    }
    return <ErrorMessage>{errors._general}</ErrorMessage>;
  }

  // Default values while loading to avoid null reference errors
  const title = dealData?.getDealById?.title || 'Loading...';
  const receiverTeam = dealData?.getDealById?.receiverTeam;

  return (
    <>
      <Helmet
        title={formatMessage(
          {
            id: 'title.deal-detail',
            defaultMessage: `Deal: ${title}`,
          },
          { title: title! }
        )}
      />

      <Header
        noMore={window.innerWidth <= 768}
        titleComponent={
          <>
            <div className={'d-flex align-items-center ml-3 non-mobile-flex'}>
              <A href={paths.deals.landing} hrefParams={{ teamId }}>
                <ArrowLeftIcon height="24" className={'pr-2 mr-1'} />
              </A>

              {canEdit && isCreator && window.innerWidth > 768 ? (
                <InputTitle dealId={dealId} title={title} isDealTitleEditing />
              ) : (
                <div className={styles.desktopDealTitle}>{title}</div>
              )}
              <div className={'pl-3'}>
                <Controls dealId={dealId} teamId={teamId} />
              </div>
              <DealNotificationToggle
                dealId={dealId}
                userDealNotifications={userDealNotifications}
                refetchUser={refreshUserData}
              />
            </div>
            <div className={'d-flex align-items-center flex-column mobile-flex'}>
              <div className="d-flex align-items-center justify-content-between w-100 px-2">
                <div className="d-flex align-items-center" style={{ minWidth: 0, flex: 1 }}>
                  <A href={paths.deals.landing} hrefParams={{ teamId }}>
                    <ArrowLeftIcon height="24" className={'mr-3'} />
                  </A>

                  <div className={styles.mobileDealTitle}>{title}</div>
                </div>
                <div className="ml-2" style={{ flexShrink: 0 }}>
                  <Controls dealId={dealId} teamId={teamId} />
                </div>
              </div>
            </div>
          </>
        }
        refetchDealData={refreshDealData}
      ></Header>

      <ContentBody className={styles.dealDetailBody}>
        {/*
        // @ts-ignore */}
        <CardContainer className={styles.cardsContainer}>
          <div className="col-md-8">
            <div className={styles.documentsContainer}>
              <DocumentTable
                  dealDocumentsData={dealDocumentsData?.getDocumentsByDeal as unknown as Document[] || []}
                  permissionsData={permissions}
                  isDealShared={!!receiverTeam}
                  isCurrentUserAdmin={permissions.isAdmin}
                  teamId={teamId}
                  alertableDocuments={alertableDocuments?.getAlertableDocumentsByTeam as unknown as Document[] || []}
                  onChecklistClick={handleChecklistClick}
                />
            </div>
          </div>
          <div className="col-md-4">
            {isCreator && (
              <ChecklistProgressCard
                dealId={dealId}
                onClick={handleChecklistClick}
              />
            )}
            <div className={styles.activitiesCardWrapper}>
              <ActivitiesCard dealData={dealData?.getDealById} dealDocumentsData={dealDocumentsData?.getDocumentsByDeal} />
            </div>
          </div>
        </CardContainer>

        {(permissions.isAdmin || canEdit) && (
          <>
            {dealData?.getDealById && (
              <DocumentTemplateModal
                isOpen={activeModal === CreateDocumentModalsEnum.TEMPLATE_SELECTOR}
                onClose={() => setActiveModal(null)}
                dealData={dealData.getDealById}
              />
            )}
            {activeModal === CreateDocumentModalsEnum.BLANK_DOCUMENT && (
              <CreateBlankDocumentModal
                isOpen={true}
                onClose={() => setActiveModal(null)}
              />
            )}
            {(activeModal === ModalActionsEnum.UPLOAD_FILE || activeModal === ModalActionsEnum.UPLOAD_DOC_FILE) && (
              <FileUploadModal
                onClose={() => setActiveModal(null)}
                isOpen={true}
                dealId={dealId}
                teamId={teamId}
                isPremiumUser={canCreateTemplates}
              />
            )}
          </>
        )}

        <SubscriptionRequiredModal
          isOpen={isSubscriptionModalActive}
          onClose={() => setSubscriptionModalActive(false)}
        />

        <ChecklistSlideout
          dealId={dealId}
          isOpen={isChecklistSlideoutOpen}
          onClose={() => setIsChecklistSlideoutOpen(false)}
        />
      </ContentBody>
    </>
  );
};

const Detail = () => {
  return (
    <CheckDealExists>
      <PrivateLayout>
        <Page />
      </PrivateLayout>
    </CheckDealExists>
  );
};

export default Detail;
